/**
 * What an inquiry row says about itself.
 *
 * The table used to title each row by its service types and subtitle it with a
 * UUID prefix. Both are unscannable: two sourcing requests from the same school
 * read identically, and "198b110c" identifies nothing to a person. What
 * distinguishes one inquiry from another is what the customer is asking for.
 */

interface Product {
  name?: string | null
  quantity?: number | null
}

interface InquiryLike {
  reference_code?: string | null
  service_type?: string | null
  service_types?: string[] | null
  product_list?: Product[] | null
  created_at?: string | null
}

export function formatLabel(value: string | null | undefined): string {
  if (!value) return '—'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** "200 × A5 notebooks", or "Security cameras +2 more" when there are several. */
export function inquiryTitle(inquiry: InquiryLike): string {
  const products = (inquiry.product_list ?? []).filter((product) => product?.name?.trim())
  if (products.length === 0) {
    // Nothing itemised yet, so the service is the most specific thing there is.
    return formatLabel(inquiry.service_type ?? inquiry.service_types?.[0])
  }

  const first = products[0]!
  const name = String(first.name).trim()
  const quantity = Number(first.quantity)
  const lead =
    Number.isFinite(quantity) && quantity > 1 ? `${quantity.toLocaleString()} × ${name}` : name

  return products.length > 1 ? `${lead} +${products.length - 1} more` : lead
}

/**
 * The services, compact. Four of them concatenated is what produced
 * "End To End Import, Product Sourcing, Pvoc Testing, Shipping Only" as a title.
 */
export function inquiryServices(inquiry: InquiryLike): string {
  const services = inquiry.service_types?.length
    ? inquiry.service_types
    : [inquiry.service_type ?? null]
  const named = services.filter(Boolean).map((service) => formatLabel(service))
  if (named.length === 0) return '—'
  return named.length > 2
    ? `${named.slice(0, 2).join(', ')} +${named.length - 2}`
    : named.join(', ')
}

/**
 * The services plus when it came in.
 *
 * The date is what separates two rows that are otherwise identical - the same
 * customer asking for the same thing twice is a real and common case, and
 * without it those rows are indistinguishable at a glance.
 */
export function inquirySubtitle(inquiry: InquiryLike): string {
  const services = inquiryServices(inquiry)
  if (!inquiry.created_at) return services

  const date = new Date(inquiry.created_at)
  if (Number.isNaN(date.getTime())) return services

  const now = new Date()
  const stamp = date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  })
  const stamped = `${services} · ${stamp}`
  // The reference leads, because it is the only part that is unique and the
  // only part an operator can quote back on a call.
  return inquiry.reference_code ? `${inquiry.reference_code} · ${stamped}` : stamped
}
